import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Grid from '@mui/material/Grid';
import {StaticImageData} from 'next/image';
import DisplayImage from '../display-image';
import {fadeInUp} from '@/animation/scroll-animation';
import dynamic from 'next/dynamic';
import parse from 'html-react-parser';
import {borderRadius, fontWeight, gradients} from '@/styles/tokens';
const AnimatedComponent = dynamic(() => import('../AnimatedComponent').then((mod) => mod.AnimatedComponent));

interface ComplianceItem {
    title: string;
    icon: string | StaticImageData;
}

interface ComplianceReadyProps {
    heading: string;
    subheading: string;
    data: ComplianceItem[];
}

const mainBoxSx = {
    background: gradients.darkSection,
    color: '#fff'
} as const;

const cardSx = {
    bgcolor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: borderRadius.lg,
    p: {xs: 3, md: 4},
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    '&:hover': {
        border: '1px solid rgba(255, 255, 255, 0.3)',
        transform: 'translateY(-5px)',
        bgcolor: 'rgba(255, 255, 255, 0.08)'
    }
} as const;

const iconWrapperSx = {
    width: 60,
    height: 60,
    mb: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
} as const;

const ComplianceReady = ({heading, subheading, data}: ComplianceReadyProps) => {
    return (
        <Box sx={mainBoxSx} className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
            <MUIContainer>
                <Box textAlign="center" maxWidth={900} margin="0 auto" mb={{xs: 6, md: 10}}>
                    <Typography variant="h2" component="h2" mb={3} fontSize={{xs: 32, sm: 48}} className="font-headers" color="#fff">
                        {heading}
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        component="p"
                        className="font-regular-text"
                        color="rgba(255,255,255,0.8)"
                        sx={{lineHeight: 1.6}}
                    >
                        {subheading}
                    </Typography>
                </Box>

                <Grid container spacing={3} justifyContent="center">
                    {data?.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                            <AnimatedComponent variants={fadeInUp} delay={index * 0.05} style={{height: '100%'}}>
                                <Box sx={cardSx}>
                                    <Box sx={iconWrapperSx}>
                                        <DisplayImage
                                            src={item.icon}
                                            alt={item.title}
                                            width={50}
                                            height={50}
                                            style={{filter: 'brightness(0) invert(1)'}}
                                        />
                                    </Box>
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontSize: '16px',
                                            fontWeight: fontWeight.regular,
                                            lineHeight: 1.4
                                        }}
                                        className="font-regular-text solution-link"
                                    >
                                        {parse(item.title)}
                                    </Typography>
                                </Box>
                            </AnimatedComponent>
                        </Grid>
                    ))}
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(ComplianceReady);
